import { useState, type Dispatch, type SetStateAction } from "react";

/**
 * Render-time change detection for a sentinel value ("adjusting state when a
 * prop changes", react.dev). Returns `true` on exactly the first render after
 * `key` changes, so the caller can re-seed local state **during render** instead
 * of from an effect — an effect would render once with the stale value, then
 * cascade a second render (`react-hooks/set-state-in-effect`).
 *
 * The pattern shipped inline in `components/isms/detail/BearbeitungForm.tsx` and
 * `components/admin/auth/ExternalAuthPanel.tsx` (their `syncedFrom` sentinels);
 * this is that pattern factored out.
 *
 * Use this directly when the re-seed is conditional (a dirty/`touched` guard) or
 * writes several unrelated pieces of state; use {@link useSyncedState} when it
 * is a single value derived from the key.
 *
 * ⚠️ `key` must be referentially stable across renders where nothing changed —
 * a primitive, or an object owned by TanStack Query (structural sharing keeps
 * those stable). A freshly allocated object/array on every render loops.
 */
export function useKeyChange(key: unknown): boolean {
  const [syncedKey, setSyncedKey] = useState<unknown>(key);
  if (!Object.is(key, syncedKey)) {
    setSyncedKey(key);
    return true;
  }
  return false;
}

/**
 * `useState` whose value is re-seeded from `compute()` whenever `key` changes,
 * evaluated during render (see {@link useKeyChange}). Between key changes it
 * behaves exactly like `useState`, so local edits survive re-renders and
 * background refetches that return an identical `key`.
 *
 * The common shape is "seed a form draft from a resolved query / an opened
 * dialog's subject":
 *
 * ```ts
 * const [checked, setChecked] = useSyncedState(
 *   version,
 *   () => new Set(version?.visibleLevelCodes ?? []),
 * );
 * ```
 *
 * `compute` is called during render and must be pure. It may not return a
 * function (the value is stored via a `useState` updater).
 */
export function useSyncedState<T>(
  key: unknown,
  compute: () => T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(compute);
  const changed = useKeyChange(key);
  if (changed) {
    setValue(() => compute());
  }
  return [value, setValue];
}
